import { isEmptyArray } from "@/utils";
import { INIT_PREFIX, MAXIMUM_GAP_DURATION, MAXIMUM_UPLOAD_AGE, SEGMENT_PREFIX } from "../const";
import { DATE_INDEX, FILENAME_INDEX, HlsDb, SELECT_FOR_UPLOAD_INDEX } from "../DB";
import { isGapFile, isInitFile, isSegmentFile } from "../extensions";

export default class DbController {
  getRead: HlsDb["getRead"];
  readAllFiles: HlsDb["readAll"];
  private isDeletingOldFiles = false;
  private isFillingGaps = false;

  constructor(private db: HlsDb) {
    this.db = db;
    this.getRead = this.db.getRead;
    this.readAllFiles = this.db.readAll;
  }

  getMetaCursor = (mode: IDBTransactionMode = "readonly", cursorOptions: Parameters<IDBObjectStore["openCursor"]>) => {
    const index = this.db.createTransaction(mode, "hls-meta");
    const request = index.openCursor(...cursorOptions);
    return request;
  };

  private deleteRemainingFiles = async ({
    dataFilesToDelete,
    metaFilesToDelete
  }: {
    dataFilesToDelete: string[];
    metaFilesToDelete: string[];
  }) => {
    const metaRemove = this.db.getDelete("hls-meta", "filename");
    const dataRemove = this.db.getDelete("hls-data", "filename");
    log.recorderDbcontroller("About to delete meta files: ", metaFilesToDelete);
    log.recorderDbcontroller("About to delete data files: ", dataFilesToDelete);
    metaFilesToDelete.forEach((filename) => metaRemove(filename));
    dataFilesToDelete.forEach((filename) => dataRemove(filename));
  };

  deleteOlderThan = async (time: number, onDiscontinuityDelete?: () => void) => {
    if (this.isDeletingOldFiles) return;
    this.isDeletingOldFiles = true;
    try {
      await this.deleteOldFiles(time, onDiscontinuityDelete);
    } catch (err) {
      log.err("'deleteOlderThan' failed", err);
    } finally {
      this.isDeletingOldFiles = false;
    }
  };

  private deleteOldFiles = async (time: number, onDiscontinuityDelete?: () => void) => {
    log.recorderDbcontroller("'deleteOldFiles' called with time", time);
    const upperBound = new Date(Date.now() - time).toISOString();
    const range = IDBKeyRange.upperBound(upperBound);
    const uploadDeadline = new Date(Date.now() - MAXIMUM_UPLOAD_AGE).toISOString();

    const pendingUploadKeys = await this.getPendingUploadKeys();
    const initFilenames = await this.getInitFilenames();

    const metaIndex = this.db.createTransaction("readwrite", "hls-meta").index(DATE_INDEX);
    const metaRequest = metaIndex.openCursor(range);

    const dataIndex = this.db.createTransaction("readwrite", "hls-data").index(DATE_INDEX);
    const dataRequest = dataIndex.openKeyCursor(range);

    let initMetaFilenamesToDelete: string[] = [];
    let initDataFilenamesToDelete: string[] = [];
    let lastInitFilename: string | null = null;
    let expiredUploads = 0;

    await Promise.all([
      new Promise<void>((resolve, reject) => {
        metaRequest.onerror = () => reject(metaRequest.error);
        metaRequest.onsuccess = (e) => {
          const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            const file = cursor.value as HlsMetaItem | null;
            if (!file) {
              log.err("Invalid cursor value for hls meta file in deleteOlderThan, deleting key", cursor.primaryKey);
              cursor.delete();
              cursor.continue();
              return;
            }
            const isInit = isInitFile(file);

            if (isInit) {
              initMetaFilenamesToDelete.push(file.filename);
            } else {
              log.recorderDbcontroller("Deleting meta file", file.filename);
              if (file.discontinuity && onDiscontinuityDelete) onDiscontinuityDelete();
              cursor.delete();
            }
            cursor.continue();
          } else resolve();
        };
      }),
      new Promise<void>((resolve, reject) => {
        dataRequest.onerror = () => reject(dataRequest.error);
        dataRequest.onsuccess = (e) => {
          const cursor = (e.target as IDBRequest<IDBCursor>).result;
          if (cursor) {
            const key = cursor.primaryKey as number;
            const initFilename = initFilenames.get(key);

            if (initFilename) {
              initDataFilenamesToDelete.push(initFilename);
              lastInitFilename = initFilename;
            } else {
              const isExpired = (cursor.key as string) <= uploadDeadline;
              if (pendingUploadKeys.has(key) && !isExpired) {
                initDataFilenamesToDelete = initDataFilenamesToDelete.filter((f) => f !== lastInitFilename);
              } else {
                if (pendingUploadKeys.has(key)) expiredUploads++;
                log.recorderDbcontroller("Deleting data file", key);
                dataIndex.objectStore.delete(key);
              }
            }
            cursor.continue();
          } else resolve();
        };
      })
    ]);

    if (expiredUploads > 0) log.err(`Deleted ${expiredUploads} segments that couldn't be uploaded in time`);

    const initFilenameToPreserve = await this.getRemainingInitFilename();
    log.recorderDbcontroller("Preserved init filename: ", initFilenameToPreserve);

    initDataFilenamesToDelete = initDataFilenamesToDelete.filter((f) => f !== initFilenameToPreserve);
    initMetaFilenamesToDelete = initMetaFilenamesToDelete.filter((f) => f !== initFilenameToPreserve);

    await this.deleteRemainingFiles({
      dataFilesToDelete: initDataFilenamesToDelete,
      metaFilesToDelete: initMetaFilenamesToDelete
    });
  };

  private getPendingUploadKeys = async () => {
    const keys = await this.db.readIndexKeys("hls-data", SELECT_FOR_UPLOAD_INDEX, 1);
    return new Set(keys.map(({ primaryKey }) => primaryKey as number));
  };

  private getInitFilenames = async () => {
    const range = IDBKeyRange.bound(INIT_PREFIX, INIT_PREFIX + "\uffff");
    const keys = await this.db.readIndexKeys("hls-data", FILENAME_INDEX, range);
    return new Map(keys.map(({ key, primaryKey }) => [primaryKey as number, key as string]));
  };

  fillSegmentGaps = async (date: string) => {
    if (this.isFillingGaps) throw Error("Previous gap filling is still running");
    this.isFillingGaps = true;
    try {
      await this.addGapEntries(date);
    } finally {
      this.isFillingGaps = false;
    }
  };

  private addGapEntries = async (date: string) => {
    const oldestSegment = await this.getFirstFoundSegmentFile("prev");
    if (!oldestSegment) {
      log.recorderDbcontroller("Not adding any gaps");
      return;
    }

    const { createdAt, duration } = oldestSegment;
    const gapStartTime = new Date(createdAt).getTime() + Number(duration) * 1000;
    const gapEndTime = new Date(date).getTime();

    const timeDifference = gapEndTime - gapStartTime;

    let seconds = timeDifference / 1000;
    const gaps: number[] = [];

    while (seconds >= MAXIMUM_GAP_DURATION) {
      gaps.push(MAXIMUM_GAP_DURATION);
      seconds -= MAXIMUM_GAP_DURATION;
    }
    if (seconds > 0.02) gaps.push(seconds);

    if (isEmptyArray(gaps)) return;

    const { initIndex, segmentIndex: startSegmentIndex } = await this.getNextUsableIndexes();

    const startDate = new Date(gapStartTime);
    let initFilename = `g${initIndex}.mp4`;
    const initPayload: HlsMetaGapItem = {
      filename: initFilename,
      initFilename: null,
      index: initIndex,
      createdAt: startDate.toISOString(),
      discontinuity: false,
      duration: null
    };

    const write = this.db.getWrite("hls-meta");
    await write(initPayload);

    let segmentIndex = startSegmentIndex;
    const segmentDate = startDate;

    log.recorderDbcontroller("gaps: ", gaps);
    const unevenGapInitIndex = initIndex + 1;

    for (const [index, gapDuration] of gaps.entries()) {
      const isLast = index === gaps.length - 1;

      if (isLast && gaps.length > 1) {
        initFilename = `g${unevenGapInitIndex}.mp4`;
        const initPayload: HlsMetaGapItem = {
          filename: initFilename,
          initFilename: null,
          index: unevenGapInitIndex,
          createdAt: new Date(segmentDate).toISOString(),
          discontinuity: false,
          duration: null
        };
        await write(initPayload);
      }

      const payload: HlsMetaGapItem = {
        filename: `g${segmentIndex}.m4s`,
        initFilename,
        index: segmentIndex,
        createdAt: new Date(segmentDate).toISOString(),
        discontinuity: true,
        duration: gapDuration.toFixed(6)
      };
      await write(payload);

      segmentDate.setMilliseconds(segmentDate.getMilliseconds() + gapDuration * 1000);
      segmentIndex++;
    }
  };

  getFirstFoundSegmentFile = (direction: IDBCursorDirection) => {
    const request = this.getMetaCursor("readonly", [null, direction]);

    return new Promise<HlsMetaItem | null>((resolve) => {
      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const file = cursor.value as HlsMetaItem;
          if (!file) {
            log.err(
              "Invalid cursor value for hls meta file in getFirstFoundSegmentFile, skipping key",
              cursor.primaryKey
            );
            cursor.continue();
            return;
          }
          if (isSegmentFile(file)) resolve(file);
          else cursor.continue();
        } else resolve(null);
      };
    });
  };

  private getRemainingInitFilename = () => {
    const request = this.getMetaCursor("readonly", [null, "next"]);

    return new Promise<string | null>((resolve, reject) => {
      const findInitInDataFiles = async (segmentFilename: string) => {
        const file = await this.getRead<HlsDataItem>("hls-data", "filename")(segmentFilename).catch(() => null);
        resolve(file?.initFilename || null);
      };
      request.onerror = () => reject(request.error);

      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const file = cursor.value as HlsMetaItem | HlsMetaGapItem | null;
          if (!file) {
            log.err(
              "Invalid cursor value for hls meta file in getRemainingInitFilename, skipping key",
              cursor.primaryKey
            );
            cursor.continue();
            return;
          }
          if (isSegmentFile(file)) {
            if (isGapFile(file)) resolve(file.initFilename);
            else findInitInDataFiles(file.filename);
          } else cursor.continue();
        } else resolve(null);
      };
    });
  };

  getNextUsableIndexes = async () => {
    const { segmentIndex, initIndex } = await this.getNextMetaIndexes();
    return {
      initIndex: Math.max(initIndex, await this.getNextDataIndex(INIT_PREFIX)),
      segmentIndex: segmentIndex || (await this.getNextDataIndex(SEGMENT_PREFIX))
    };
  };

  private getNextDataIndex = async (prefix: string) => {
    const range = IDBKeyRange.bound(prefix, prefix + "\uffff");
    const files = await this.db.readIndexKeys("hls-data", FILENAME_INDEX, range);
    let nextIndex = 0;
    files.forEach(({ key }) => {
      const index = parseInt((key as string).slice(prefix.length));
      if (index >= nextIndex) nextIndex = index + 1;
    });
    return nextIndex;
  };

  private getNextMetaIndexes = () => {
    const request = this.getMetaCursor("readonly", [null, "prev"]);

    let segmentIndex: number;
    let initIndex: number;

    return new Promise<{ segmentIndex: number; initIndex: number }>((resolve, reject) => {
      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const file = cursor.value as HlsMetaItem | null;
          if (!file) {
            log.err("Invalid cursor value for hls meta file in getNextUsableIndexes, skipping key", cursor.primaryKey);
            cursor.continue();
            return;
          }
          if (!segmentIndex && isSegmentFile(file)) {
            segmentIndex = file.index + 1;
          }
          if (!initIndex && isInitFile(file)) {
            initIndex = file.index + 1;
          }

          if (segmentIndex != null && initIndex != null) {
            log.recorderDbcontroller("Found both indexes ", initIndex, "and", segmentIndex);
            resolve({ segmentIndex, initIndex });
          } else {
            cursor.continue();
          }
        } else {
          log.recorderDbcontroller("Cursor finished");
          resolve({
            segmentIndex: segmentIndex || 0,
            initIndex: initIndex || 0
          });
        }
      };
      request.onerror = (e) => {
        log.recorderDbcontroller("Cursor failed: ", e);
        reject("Cursor failed at 'getNextUsableIndexes'");
      };
    });
  };
}
